/**
 * useBillingProfile — F-002 Phase 4 (IMPL-P4-002).
 *
 * Context + Provider + hook for the current account's billing profile.
 * Mirror of `useAuthSession` for the F-002 feature: the Provider fetches
 * `/api/v1/billing-profile` on mount, exposes
 * `{ profile, status, isComplete, fiscalReverificationAt, hasFattureEmesse,
 *    refresh, save }`, and serves as the single source of truth for the
 * profilo feature plus the global completeness Badge.
 *
 * Status semantics:
 *  - `loading` — initial mount, /api/v1/billing-profile in flight
 *  - `present` — API returned a profile row
 *  - `absent`  — API responded 404 (no profile yet — onboarding case)
 *  - `error`   — network or server failure (UI keeps working but the
 *                badge falls back to "incompleto" so /app/profilo stays
 *                accessible)
 *
 * `hasFattureEmesse` is hard-coded to `false` in V1: the SDI invoices feature
 * (F-011) will derive this flag from the invoices count query. The flag is
 * threaded through here so the form can show the SDI-change confirmation
 * dialog without coupling the form to the future invoices client.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { isBillingProfileComplete, type BillingProfileUpsertInput } from '@app/shared';

import {
  getBillingProfile,
  upsertBillingProfile,
  type BillingProfileView,
} from '../lib/profilo-client';

export type BillingProfileStatus = 'loading' | 'present' | 'absent' | 'error';

export interface BillingProfileState {
  profile: BillingProfileView | null;
  status: BillingProfileStatus;
  isComplete: boolean;
  fiscalReverificationAt: string | null;
  /**
   * True when the account has already emitted at least one SDI invoice.
   * V1: always `false` — TODO(F-011): derive from invoices API once available.
   */
  hasFattureEmesse: boolean;
  refresh: () => Promise<void>;
  save: (input: BillingProfileUpsertInput) => Promise<BillingProfileView>;
}

const BillingProfileContext = createContext<BillingProfileState | null>(null);

export function BillingProfileProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<BillingProfileView | null>(null);
  const [status, setStatus] = useState<BillingProfileStatus>('loading');

  const refresh = useCallback(async () => {
    try {
      const res = await getBillingProfile();
      if (res) {
        setProfile(res);
        setStatus('present');
      } else {
        setProfile(null);
        setStatus('absent');
      }
    } catch {
      // Network or unexpected status: collapse to 'error' rather than throw —
      // the badge stays visible (defaults to incomplete) and the user can
      // still navigate to /app/profilo to retry by re-rendering the page.
      setProfile(null);
      setStatus('error');
    }
  }, []);

  const save = useCallback(
    async (input: BillingProfileUpsertInput): Promise<BillingProfileView> => {
      const updated = await upsertBillingProfile(input);
      setProfile(updated);
      setStatus('present');
      return updated;
    },
    [],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<BillingProfileState>(
    () => ({
      profile,
      status,
      isComplete: isBillingProfileComplete(profile ?? null),
      fiscalReverificationAt: profile?.fiscalReverificationAt ?? null,
      // TODO(F-011): replace with real invoices count from /api/v1/invoices.
      hasFattureEmesse: false,
      refresh,
      save,
    }),
    [profile, status, refresh, save],
  );

  return (
    <BillingProfileContext.Provider value={value}>{children}</BillingProfileContext.Provider>
  );
}

export function useBillingProfile(): BillingProfileState {
  const ctx = useContext(BillingProfileContext);
  if (!ctx) {
    throw new Error('useBillingProfile must be used inside <BillingProfileProvider>');
  }
  return ctx;
}
